import React from "react"
import { useTranslation } from "react-i18next"
import { sub } from "date-fns"

import ProtectedLayout from "layouts/Protected"
import { useAlert, useAuth, useDrawer } from "hooks"

import FormElement from "components/Form/FormElement"
import browserStorageService from "services/browserStorage"
import dateService from "services/date"
import { historyTypes, onGoingHistoryOperationNamesList, confirmedHistoryOperationNamesList } from "utils/constants"
import { getJsonPipeFlow } from "utils/jsonPipe"
import { HistoryIcon } from "components/Elements/Icons"

import { HistoryTable, HistorySubFilters } from "./Partials"
import { ExportData } from "features/history/Partials/ExportData"

import "./History.scoped.scss"

const HISTORY_MAX_LENGTH = 3000

const historyStatusEnum = {
    loading: "loading",
    complete: "complete",
    errored: "errored",
}

const defaultFormFields = {
    bikeStatus: historyTypes.onGoing.value,
}

const transformArrayIntoKeyObject = (array, keyProperty) => {
    return array.reduce((acc, curr) => {
        return {
            ...acc,
            [curr[keyProperty]]: curr
        }
    }, {})
}

const sortBikesByDateProperty = (dateProperty) => (bikeA, bikeB) => dateService.getTimestamp(bikeB?.[dateProperty]) - dateService.getTimestamp(bikeA?.[dateProperty])

const compareObjectByLabel = (a, b, label) => (a?.[label] > b?.[label] ? a : b)

export const HistoryPage = () => {
    const [formFields, setFormFields] = React.useState(defaultFormFields)
    const [historyStatus, setHistoryStatus] = React.useState()
    const [history, setHistory] = React.useState([])
    const [errors, setErrors] = React.useState([])

    const { t } = useTranslation()
    const { alert } = useAlert()
    const { logout, entity } = useAuth()
    const { showDrawer } = useDrawer()

    const isHistoryLoading = historyStatus === historyStatusEnum.loading

    const lastMonthFilteredBikesDate = sub(new Date(), { months: 1 })

    const entityId = entity.entity_id

    const handleInputChange = (event) => {
        const target = event.target
        const value = target.type === "checkbox" ? target.checked : target.value
        const name = target.name
        setFormFields({
            ...formFields,
            [name]: value
        })

        const formReady = !(errors.length > 0)
        if (formReady) {
            getBikesActivity(value)
        }
    }

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
    }, [])

    const reconciliatedBikes = (localBikes, updatedBikes) => {
        // The bikes are saved as an object with key/value
        // to avoid duplicate keys (ie duplicate bike)
        const reconciliatedBikesObject = {
            ...transformArrayIntoKeyObject(localBikes, "bike_id"),
            ...transformArrayIntoKeyObject(updatedBikes, "bike_id"),
        }
        return Object.values(reconciliatedBikesObject)
    }

    const sortBikesByUnknownDateProperty = (bikes) => {
        if (!bikes || bikes.length === 0) return null
        // if we don't know the date property, we could parse the given
        // existing date properties and check the one that works
        
        const existingDateProperties = Object.values(historyTypes).map(bike => bike.dateProperty)
        for (const dateProperty of existingDateProperties) {
            if (bikes[0].hasOwnProperty(dateProperty)) {
                return sortBikesByDateProperty(dateProperty)
            }
        }
        return null
    }

    const fetchStreamBikesActivity = async ({
        bikesActivityProps,
        handleCompleteResponse
    }) => {
        try {
            setHistoryStatus(historyStatusEnum.loading)

            // EntityId is passed to differentiate the history between two different account
            const getStorageNameWithEntityId = `${bikesActivityProps.storageName}_${entityId}`

            const hasLocalPreregisteredBikes = browserStorageService.get(getStorageNameWithEntityId, { priority: "sessionStorage" })
            const parsedPreregisteredBikes = hasLocalPreregisteredBikes ? JSON.parse(hasLocalPreregisteredBikes) : null

            // Date 30 days ago and format date to compare with bike object
            const date30DaysAgo = dateService.formatDate(lastMonthFilteredBikesDate, "yyyy-MM-dd HH:mm:SS.SSS")

            // If a store has no activity, the localStorage can send an empty Array. We check this with savedBikes.length > 0
            const savedBikes = parsedPreregisteredBikes ? parsedPreregisteredBikes.filter(b => b.create_date > date30DaysAgo) : []

            // Case 1 : Retrieve date from the most recent bike registered
            let lastSavedBikeDate = ""
            if (savedBikes.length > 0) {
                const mostRecentBike = Object.values(savedBikes).reduce((a, b) => compareObjectByLabel(a, b, bikesActivityProps.dateProperty))
                lastSavedBikeDate = mostRecentBike?.[bikesActivityProps.dateProperty]
            }

            // Case 2 : Retrieve date from 30 days ago
            const lastMonthFilteredBikesDateToUTC = dateService.formatDateToUTC(lastMonthFilteredBikesDate)

            const preRegisteredHistoryRoute = savedBikes.length > 0
                ? `${bikesActivityProps.route}?${bikesActivityProps.routeParam}=${lastSavedBikeDate}`
                : `${bikesActivityProps.route}?${bikesActivityProps.routeParam}=${lastMonthFilteredBikesDateToUTC}`
            let streamedBikesObject = {}

            const onPreregisteredHistoryProgress = (data) => {
                // The data streamed is saved as an object with key/value
                // to avoid duplicate keys (ie duplicate bike)
                streamedBikesObject = {
                    ...streamedBikesObject,
                    [data.bike_id]: data
                }
            }
            const onPreregisteredHistoryError = (error) => {
                setHistoryStatus(historyStatusEnum.errored)
                throw bikesActivityProps.getError(error)
            }
            const onPreregisteredHistoryComplete = () => {
                const streamedBikes = Object.values(streamedBikesObject)
                const bikesHistory = hasLocalPreregisteredBikes
                    ? (streamedBikes.length > 0)
                        ? reconciliatedBikes(savedBikes, streamedBikes)
                        : savedBikes
                    : streamedBikes
                // Remove delete bikes from Array (status 9). Available on pre-saved-bike
                let bikeHistoryFiltered = bikesHistory.filter(bike => bike?.status !== 9)
                // Only keep a given quantity of bikes (--> filtering func), and the most recent ones (--> sorting func)
                // (a reason to do that is that the bikesArray is then stringified before being saved in storage.
                // But some browsers such as Safari cannot save in storage a too long string.)
                if (bikeHistoryFiltered && bikeHistoryFiltered.length > 0) {
                    bikeHistoryFiltered = bikeHistoryFiltered
                                            .sort(sortBikesByUnknownDateProperty(bikesHistory))
                                            .filter((bike, index) => index < HISTORY_MAX_LENGTH)
                }
                browserStorageService.set(getStorageNameWithEntityId, JSON.stringify(bikeHistoryFiltered), { priority: "sessionStorage" })
                setHistoryStatus(historyStatusEnum.complete)
                handleCompleteResponse(bikeHistoryFiltered)
            }

            await getJsonPipeFlow({
                apiRoute: preRegisteredHistoryRoute,
                onProgress: onPreregisteredHistoryProgress,
                onComplete: onPreregisteredHistoryComplete,
                onError: onPreregisteredHistoryError,
                utils: {
                    alert,
                    logout
                }
            })
        } catch (error) {
            console.error(error)
            const erroMsg = error?.message?.toString()
            if (erroMsg) alert("error", erroMsg)
            if (["NotAuthorizedException"].includes(error?.code)) logout()
        }
    }

    const getBikesActivity = async (historyValue) => {
        switch (historyValue) {
            case historyTypes.onGoing.value:
                await fetchStreamBikesActivity({
                    bikesActivityProps: historyTypes.onGoing,
                    handleCompleteResponse: (response) => {
                        const onGoingBikes = response.filter(bike => [...historyTypes.onGoing.operationNameList].includes(bike?.operation))
                        setHistory(onGoingBikes)
                    }
                })
                break
            case historyTypes.activated.value:
                await fetchStreamBikesActivity({
                    bikesActivityProps: historyTypes.activated,
                    handleCompleteResponse: (response) => {
                        const activatedBikes = response.filter(bike => [...historyTypes.activated.operationNameList].includes(bike?.operation))
                        // TODO: includes the historyTypes.secondHand.operationNameList in the test array
                        setHistory(activatedBikes)
                    }
                })
                break
            case historyTypes.preregistered.value:
                await fetchStreamBikesActivity({
                    bikesActivityProps: historyTypes.preregistered,
                    handleCompleteResponse: (response) => {
                        setHistory(response)
                    }
                })
                break
            case historyTypes.backAndBuy.value:
                await fetchStreamBikesActivity({
                    bikesActivityProps: historyTypes.backAndBuy,
                    handleCompleteResponse: (response) => {
                        const backAndBuyBikes = response.filter(bike => [...historyTypes.backAndBuy.operationNameList].includes(bike?.operation))
                        setHistory(backAndBuyBikes)
                    }
                })
                break
            default:
                break
        }
    }

    const fetchData = React.useCallback(
        () => getBikesActivity(historyTypes.onGoing.value),
    []) // eslint-disable-line

    // fetch data on first load
    React.useEffect(() => {
		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

    const openFormExport = () => {
        showDrawer(
            DrawerExportData,
            {
                body: ExportData,
            },
            { style: { maxWidth: "800px" } }
        )
    }

    return (
        <ProtectedLayout className="c-history px-7 py-7">
            <div className="l-container">

                <div className="c-section-title mb-7 flex flex-row">
                    <div className="c-section-title__content justify-between">
                        <div className="flex items-center justify-start w-full">
                            <HistoryIcon size={40} />
                            <h1 className="c-section-title__title c-h2 text-primary font-medium ml-5">
                                {t("history.title", { ns: "partner" })}
                            </h1>
                        </div>

                        <button
                            type="button"
                            className="c-btn c-btn--primary px-7"
                            onClick={() => openFormExport()}
                        >
                            {t("history.export.exportButton", { ns: "partner" })}
                        </button>
                    </div>
                </div>

                <div className="flex flex-row justify-between">
                    <form className="c-history__tabs mb-5 px-7">
                        <FormElement
                            value={formFields.bikeStatus}
                            name="bikeStatus"
                            type="radio"
                            options={Object.values(historyTypes)}
                            className=""
                            classNameOptionsContainer="flex w-full"
                            classNameLabel=""
                            classNameOption="mx-5"
                            disabled={isHistoryLoading}
                            required={true}
                            onValidate={handleValidation}
                            onChange={handleInputChange}
                        />
                    </form>
                    {isHistoryLoading &&
                        <div className="u-relative mr-7">
                            <div className="c-spinner u-absolute"></div>
                        </div>
                    }
                </div>

                <div className="c-card p-5 md:p-12">
                    {/* For "ON-GOING" and "BACK-AND-BUY-BIKES", we adding filters. We need to pass by a subcomponent to filter and display bikes */}
                    {formFields.bikeStatus === "ON-GOING" && <HistorySubFilters data={history} filters={onGoingHistoryOperationNamesList} bikeType={formFields.bikeStatus} />}

                    {formFields.bikeStatus === "BACK-AND-BUY-BIKES" && <HistorySubFilters data={history} filters={confirmedHistoryOperationNamesList} bikeType={formFields.bikeStatus} />}

                    {(formFields.bikeStatus === "SAVED-BIKES" || formFields.bikeStatus === "PRE-SAVED-BIKES") &&
                        <HistoryTable
                            data={history}
                            bikeType={formFields.bikeStatus}
                            className="pb-7"
                        />
                    }
                </div>
            </div>
        </ProtectedLayout>
    )
}

const DrawerExportData = ({ body, bodyProps, callback }) => {
    const { hideDrawer } = useDrawer()
    const validate = () => {
        hideDrawer()
        callback()
    }
    return (
        <div className="p-12">
            {React.createElement(
                body,
                {
                    ...bodyProps,
                    onFormProfilSuccess: validate
                }
            )}
        </div>
    )
}
